import { SenderRole } from '../helpers/enums';
import type { AssistantMessage, Message } from '../types';
import { DARK_THEME, LIGHT_THEME, THEME_SHADOWS } from './constants';
import type { ExtendedThemeConfig, ThemeMode } from './types';

/**
 * Get theme configuration based on mode
 */
export const getThemeConfig = (mode: ThemeMode): ExtendedThemeConfig => {
  return mode === 'dark' ? DARK_THEME : LIGHT_THEME;
};

/**
 * Get theme-specific shadows
 */
export const getThemeShadows = (mode: ThemeMode) => {
  return THEME_SHADOWS[mode];
};

/**
 * Convert hex color to rgba with opacity
 */
export const hexToRgba = (hex: string, opacity: number): string => {
  // Remove # if present
  const cleanHex = hex.replace('#', '');

  // Parse RGB values
  const r = parseInt(cleanHex.substr(0, 2), 16);
  const g = parseInt(cleanHex.substr(2, 2), 16);
  const b = parseInt(cleanHex.substr(4, 2), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Get chart colors for specific chart type
 */
export const getChartColors = (
  theme: ExtendedThemeConfig,
  type: 'default' | 'categorical' | 'sequential' | 'diverging' = 'default'
): string[] => {
  return theme.chartColors[type];
};

/**
 * Get color with theme fallback
 */
export const getColorWithFallback = (
  theme: ExtendedThemeConfig,
  colorKey: keyof ExtendedThemeConfig['colors'],
  fallback?: string
): string => {
  const color = theme.colors[colorKey];
  if (Array.isArray(color)) {
    return color[0] || fallback || '#000000';
  }
  return color || fallback || '#000000';
};

/**
 * Create theme-aware styled-components props
 */
export const createThemeProps = (theme: ExtendedThemeConfig) => ({
  theme: {
    mode: theme.mode,
    colors: theme.colors,
    tokens: theme.tokens,
    chartColors: theme.chartColors,
    shadows: getThemeShadows(theme.mode)
  }
});

/**
 * Generate AG-Grid theme configuration
 */
export const createAgGridTheme = (theme: ExtendedThemeConfig, baseTheme: any) => {
  const isDark = theme.mode === 'dark';

  return baseTheme.withParams({
    backgroundColor: isDark ? '#292929' : '#ffffff',
    foregroundColor: isDark ? '#EEEEEE' : '#000000',
    headerBackgroundColor: isDark ? '#383838' : '#f8f9fa',
    borderColor: isDark ? '#595959' : '#e1e8ed',
    oddRowBackgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
    spacing: 8,
    borderRadius: 6
  });
};

/**
 * Generate ECharts theme configuration for charts
 */
export const createEChartsTheme = (theme: ExtendedThemeConfig) => {
  return {
    textColor: theme.colors.chartText,
    borderColor: theme.colors.chartBorder,
    tooltipBg: theme.colors.chartTooltipBg,
    labelColor: theme.colors.chartLabel,
    splitLineColor: theme.colors.chartSplitLine,
    backgroundColor: theme.colors.mainBg
  };
};

/**
 * Get responsive color intensity based on theme
 */
export const getIntensityForTheme = (
  theme: ExtendedThemeConfig,
  baseColor: string,
  lightIntensity: number = 0.1,
  darkIntensity: number = 0.3
): string => {
  const intensity = theme.mode === 'dark' ? darkIntensity : lightIntensity;
  return hexToRgba(baseColor, intensity);
};

/**
 * Theme-aware focus styles
 */
export const getFocusStyles = (theme: ExtendedThemeConfig) => ({
  outline: 'none',
  borderColor: theme.colors.primary,
  boxShadow: `0 0 0 3px ${hexToRgba(theme.colors.primary, 0.1)}`
});

/**
 * Theme-aware button styles
 */
export const getButtonStyles = (
  theme: ExtendedThemeConfig,
  variant: 'primary' | 'secondary' | 'ghost' = 'primary'
) => {
  const baseStyles = {
    borderRadius: theme.tokens.radiusMd,
    transition: theme.tokens.transitionFast,
    fontWeight: '500',
    cursor: 'pointer',
    border: 'none'
  };

  switch (variant) {
    case 'primary':
      return {
        ...baseStyles,
        backgroundColor: theme.colors.primary,
        color: theme.colors.userBubbleText,
        '&:hover': {
          backgroundColor: theme.colors.primaryDark
        },
        '&:focus': getFocusStyles(theme)
      };

    case 'secondary':
      return {
        ...baseStyles,
        backgroundColor: 'transparent',
        color: theme.colors.mainText,
        border: `1px solid ${theme.colors.messageBoxBorder}`,
        '&:hover': {
          backgroundColor: getIntensityForTheme(theme, theme.colors.mainText, 0.05, 0.1)
        },
        '&:focus': getFocusStyles(theme)
      };

    case 'ghost':
      return {
        ...baseStyles,
        backgroundColor: 'transparent',
        color: theme.colors.mainText,
        '&:hover': {
          backgroundColor: getIntensityForTheme(theme, theme.colors.mainText, 0.05, 0.1)
        },
        '&:focus': getFocusStyles(theme)
      };

    default:
      return baseStyles;
  }
};

// Helper to sort response blocks by priority in assistant messages
export const sortMessageBlocks = (messages: Message[]): Message[] => {
  return messages.map((message) => {
    if (message.message_type === SenderRole.ASSISTANT) {
      const assistantMessage = message as AssistantMessage;
      if (assistantMessage.response_blocks) {
        return {
          ...assistantMessage,
          response_blocks: assistantMessage.response_blocks.sort(
            (a: any, b: any) => a.priority - b.priority
          )
        };
      }
    }
    return message;
  });
};
